import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Monitor,
  Users,
  Activity,
  Receipt,
  ClipboardList,
  Coins,
  Clock,
  Shield,
  ClipboardCheck,
  SlidersHorizontal,
  Plug,
  ListChecks,
  Bell,
  BarChart3,
  Wallet,
  TrendingUp,
  Ticket,
  Layers,
  Smartphone,
  Settings as SettingsIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { adminUsers } from '@/api/admin';

interface NavItem {
  id: string;
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  emphasis?: boolean;
  badge?: string;
}

interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    // 真实订单 = 唯一真实 hedging 账户口径(真金白银现金流)
    id: 'real',
    label: '真实订单',
    items: [
      { id: 'dashboard',     to: '/dashboard',     label: '仪表板',        icon: LayoutDashboard, emphasis: true },
      { id: 'live_screen',   to: '/live',          label: '资金账户大屏',   icon: Monitor,         emphasis: true },
      { id: 'platform_pnl',  to: '/platform-pnl',  label: '平台经营',      icon: TrendingUp,      emphasis: true },
      { id: 'orders',        to: '/orders',        label: '真实成交明细',   icon: Receipt },
      { id: 'reports',       to: '/reports',       label: '报表中心',      icon: BarChart3 },
    ],
  },
  {
    // 用户管理 = App 用户虚拟账本口径(per-user)
    id: 'user',
    label: '用户管理',
    items: [
      { id: 'employees',     to: '/employees',     label: '注册用户管理',   icon: Users,           emphasis: true },
      { id: 'user_orders',   to: '/user-orders',   label: '用户订单',      icon: ClipboardList },
      { id: 'audit_signals', to: '/signals',       label: '跟单审计',      icon: Activity },
      { id: 'assessment',    to: '/assessment',    label: '考核记录',      icon: ClipboardCheck },
      { id: 'profit_share',  to: '/profit-share',  label: '月度分润',      icon: Wallet },
    ],
  },
  {
    id: 'config',
    label: '交易配置',
    items: [
      { id: 'symbols',        to: '/symbols',           label: '品种与参数',   icon: Coins },
      { id: 'session',        to: '/session',           label: '交易时段配置', icon: Clock },
      { id: 'trading_groups', to: '/trading-groups',    label: '交易时段组',   icon: Layers },
      { id: 'risk',           to: '/risk',              label: '风控规则',    icon: Shield },
      { id: 'assessment_cfg', to: '/assessment-config', label: '考核设置',    icon: SlidersHorizontal },
    ],
  },
  {
    id: 'ops',
    label: '运维管理',
    items: [
      { id: 'alerts',       to: '/alerts',       label: '告警中心',      icon: Bell },
      { id: 'ctids',        to: '/ctids',        label: 'cTID 与连接',   icon: Plug },
      { id: 'audit_log',    to: '/audit-log',    label: '审计日志',      icon: ListChecks },
      { id: 'invite_codes', to: '/invite-codes', label: '邀请码管理',     icon: Ticket },
      { id: 'app_version',  to: '/app-version',  label: 'App 版本管理',   icon: Smartphone },
      { id: 'settings',     to: '/settings',     label: '系统设置',      icon: SettingsIcon },
    ],
  },
];

export function SideNav() {
  // 用户总数 badge 只取一条;queryKey 需和完整列表区分,避免 pageSize=1 污染表格缓存。
  const employeesQ = useQuery({
    queryKey: ['admin', 'users', 'badge', { pageSize: 1 }],
    queryFn: () => adminUsers.list({ pageSize: 1 }),
    staleTime: 60_000,
  });
  const employeesBadge = employeesQ.data ? String(employeesQ.data.total) : undefined;

  return (
    <aside className="hidden lg:flex w-56 shrink-0 flex-col border-r border-line bg-bg-1">
      <nav className="flex-1 overflow-y-auto p-3 scrollbar-thin">
        {NAV_SECTIONS.map((section, sectionIndex) => (
          <div
            key={section.id}
            className={cn('space-y-0.5', sectionIndex > 0 && 'mt-3 border-t border-line/70 pt-3')}
          >
            <div className="px-3 pb-1 text-[10px] font-semibold tracking-wider text-ink-3">
              {section.label}
            </div>
            {section.items.map((item) => {
              const badge = item.id === 'employees' ? employeesBadge : item.badge;
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-brand/10 text-brand-dim font-semibold'
                        : item.emphasis
                          ? 'text-ink-1 hover:bg-bg-3'
                          : 'text-ink-2 hover:bg-bg-3 hover:text-ink-1',
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {badge && (
                    <span className="rounded-md bg-bg-3 px-1.5 py-0.5 text-[10px] font-medium text-ink-2 group-[.active]:bg-brand/20">
                      {badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="border-t border-line p-3 text-[10px] uppercase tracking-wider text-ink-3">
        admin.superaios.com · v0.1.0
      </div>
    </aside>
  );
}
